"use client";

import React from "react";
import styled from "styled-components";
import { useRouter } from "next/navigation";
import { RiHomeLine, RiArrowLeftLine } from "@remixicon/react";

import { ButtonGameRegular } from "@/components/ui/buttons/button-game-regular";
import {
  ButtonIcon,
  ButtonIconSize,
} from "@/components/ui/buttons/button-icon";
import ChefsDraw from "@/components/icons/ChefsDraw";

/**
 * Styled Components
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  position: relative;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 600px;
  text-align: center;
  gap: 24px;
`;

const IconWrapper = styled.div`
  margin-bottom: 20px;
  opacity: 0.8;
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }
`;

const Title = styled.h1`
  font-size: 72px;
  font-weight: 800;
  color: #2d3748;
  margin: 0;
  font-family: ${({ theme }) => theme.Fonts.ttNormsProBold};
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #4a5568;
  margin: 0 0 16px 0;
  font-family: ${({ theme }) => theme.Fonts.ttNormsProBold};

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Description = styled.p`
  font-size: 18px;
  color: #718096;
  margin: 0 0 32px 0;
  line-height: 1.6;
  font-family: ${({ theme }) => theme.Fonts.ttNormsProNormal};

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const BackButton = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
`;

const FloatingCircle = styled.div<{
  $delay: number;
  $size: number;
  $left: number;
}>`
  position: absolute;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  left: ${(props) => props.$left}%;
  animation: floatUp 8s ease-in-out infinite;
  animation-delay: ${(props) => props.$delay}s;

  @keyframes floatUp {
    0% {
      transform: translateY(100vh) rotate(0deg);
      opacity: 0;
    }
    10% {
      opacity: 0.3;
    }
    90% {
      opacity: 0.3;
    }
    100% {
      transform: translateY(-100px) rotate(360deg);
      opacity: 0;
    }
  }
`;

export const NotFoundClient = () => {
  const router = useRouter();

  const handleGoHome = () => {
    router.push("/");
  };

  const handleGoBack = () => {
    router.back();
  };

  return (
    <Container>
      <FloatingElements>
        <FloatingCircle $delay={0} $size={60} $left={10} />
        <FloatingCircle $delay={2} $size={40} $left={20} />
        <FloatingCircle $delay={4} $size={80} $left={80} />
        <FloatingCircle $delay={1} $size={30} $left={70} />
        <FloatingCircle $delay={3} $size={50} $left={90} />
      </FloatingElements>

      <BackButton>
        <ButtonIcon
          icon={<RiArrowLeftLine size={44} />}
          onClick={handleGoBack}
          size={ButtonIconSize.LARGE}
          $backgroundColor="white"
          $borderColor="white"
          $iconColor="black"
          testId="back-button"
          aria-label="Go back"
        />
      </BackButton>

      <ContentWrapper>
        <IconWrapper>
          <ChefsDraw width={200} height={200} />
        </IconWrapper>

        <Title>404</Title>

        <Subtitle>Oops! Page not found</Subtitle>

        <Description>
          Looks like this page went off to cook somewhere else. Don&apos;t
          worry, our chefs are working to bring it back.
        </Description>

        <ButtonsContainer>
          <ButtonGameRegular
            text="Go home"
            color="yellow500"
            borderColor="yellow600"
            textColor="black"
            onClick={handleGoHome}
            testId="home-button"
            icon={<RiHomeLine size={20} />}
          />

          <ButtonGameRegular
            text="Go back"
            color="gray100"
            borderColor="gray300"
            textColor="gray400"
            onClick={handleGoBack}
            testId="back-button-alt"
          />
        </ButtonsContainer>
      </ContentWrapper>
    </Container>
  );
};
